import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  CreateHelpCategoryRequest,
  UpdateHelpCategoryRequest,
} from '@nucleus-commerce/api-client/admin';

import { helpCenterApi } from '../api/help-center-api';

import { helpCenterKeys } from './help-center-keys';

/**
 * Articles embed their category's handle, name and visibility (which decides `live`), so every
 * category change refetches articles.
 */
function refresh(queryClient: ReturnType<typeof useQueryClient>, storeId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: helpCenterKeys.categories(storeId) }),
    queryClient.invalidateQueries({ queryKey: helpCenterKeys.articles(storeId) }),
  ]);
}

export function useCreateHelpCategory(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateHelpCategoryRequest) => helpCenterApi.createCategory(storeId, body),
    meta: { successMessage: 'helpCenter:toasts.categoryCreated', silentError: true },
    onSuccess: () => refresh(queryClient, storeId),
  });
}

export function useUpdateHelpCategory(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateHelpCategoryRequest }) =>
      helpCenterApi.updateCategory(storeId, id, body),
    meta: { successMessage: 'helpCenter:toasts.categoryUpdated', silentError: true },
    onSuccess: () => refresh(queryClient, storeId),
  });
}

export function useDeleteHelpCategory(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => helpCenterApi.removeCategory(storeId, id),
    meta: { successMessage: 'helpCenter:toasts.categoryDeleted', silentError: true },
    onSuccess: () => refresh(queryClient, storeId),
  });
}
